import { ReactNode, useMemo } from 'react';

import {
  Area,
  Bar,
  ChartContainer,
  ChartGrid,
  ChartTooltip,
  ChartTooltipProps,
  Line,
  SxProps,
  TimeXAxis,
  YAxis,
  Box,
} from '@cast/design-system';

import { useAnalyzeWithOpsPilot } from '@cast/features-organization-shared-opspilot';
import { useIsAnimationActive } from 'components/chart';
import { useReactRouterStrategy } from 'components/chart/hooks';
import { HiddenDot } from 'components/chart/line/HiddenDot';
import { QUERY_PARAM_KEYS } from 'features/cost-report/hooks/useCostReportTimeRangeSelection';
import { CostReportChartDataPoint } from 'features/cost-report/types/charts';
import { ChartConfig } from 'types/charts';
import { TimeSeries } from 'types/metrics';
import { attachTimeTicks } from 'utils/chart';
import { isE2E } from '@cast/utils';

import { collapseCloseTimePoints, getFutureDatapointIndex } from './utils';
import { CostReportChartType } from '../../types/costOverTime';
import { isPastDataPoint } from '../../utils/costOverTime';

export type TickChartDataPoint<T> = T & {
  timestamp: string;
  chartTimestampUnix: number;
};

export type CostReportChartProps<T extends object> = {
  chartType: CostReportChartType;
  data: TimeSeries<T>;
  isLoading?: boolean;
  height: number | string;
  containerMarginTop?: number;
  config: ChartConfig[];
  xAxisProps?: Record<string, unknown>;
  yAxisProps?: Record<string, unknown>;
  sx?: SxProps;
  chartSx?: SxProps;
  hideXAxisOuterTicks?: boolean;
  cursorWidth?: number;
  testId?: string;
  syncId?: string;
  timezone?: string;
  tooltipHeader?: ChartTooltipProps<TickChartDataPoint<T>>['header'];
  tooltipFooter?: ChartTooltipProps<TickChartDataPoint<T>>['footer'];
  tooltipFormatValue?: (value: string | number | undefined) => ReactNode;
  hideSeriesInTooltip?: boolean;
  tooltipAlwaysActive?: ChartTooltipProps<
    TickChartDataPoint<T>
  >['alwaysActive'];
  TooltipComponent?: ChartTooltipProps<
    TickChartDataPoint<T>
  >['TooltipComponent'];
  children?: ReactNode;
  /**
   * Build prompt for "Analyze with OpsPilot" feature.
   * If provided and chatbot is enabled, the button will appear automatically.
   */
  buildPrompt?: (range?: { from: number; to: number }) => string | null;
};

const useCostReportZoomState = useReactRouterStrategy.bind(null, {
  queryParamsKeys: QUERY_PARAM_KEYS,
});

export const CostReportChart = <T extends CostReportChartDataPoint>({
  chartType,
  data,
  isLoading,
  height,
  containerMarginTop,
  config,
  xAxisProps,
  yAxisProps,
  sx,
  chartSx,
  testId,
  timezone,
  syncId,
  tooltipHeader,
  tooltipFooter,
  tooltipFormatValue,
  hideSeriesInTooltip = false,
  tooltipAlwaysActive,
  TooltipComponent,
  children,
  buildPrompt,
}: CostReportChartProps<T>) => {
  const analyzeWithOpsPilot = useAnalyzeWithOpsPilot({ buildPrompt });
  const isAnimationActive = useIsAnimationActive(data);
  const seriesData = useMemo((): TimeSeries<T> => {
    if (chartType !== CostReportChartType.BAR) {
      return data;
    }
    return collapseCloseTimePoints(data);
  }, [chartType, data]);
  const chartData = useMemo(() => attachTimeTicks(seriesData), [seriesData]);
  const forecastStartIndex = getFutureDatapointIndex(seriesData);
  const activeConfigs = useMemo(
    () => config.filter(({ enabled }) => enabled),
    [config]
  );
  const areaConfigs = activeConfigs.filter(({ type }) => {
    return type === CostReportChartType.AREA;
  });

  const nonAreaConfigs = activeConfigs.filter(({ type }) => {
    return type !== CostReportChartType.AREA;
  });

  const barConfig = useMemo(() => {
    if (chartType !== CostReportChartType.BAR) return undefined;
    const seriesConfig = activeConfigs.find(
      (c) => c.type === CostReportChartType.BAR
    )?.config;
    if (
      seriesConfig?.barSize === undefined &&
      seriesConfig?.maxBarSize === undefined
    ) {
      return undefined;
    }
    return {
      ...(seriesConfig.barSize !== undefined
        ? { barSize: seriesConfig.barSize }
        : {}),
      ...(seriesConfig.maxBarSize !== undefined
        ? { maxBarSize: Number(seriesConfig.maxBarSize) }
        : {}),
    };
  }, [chartType, activeConfigs]);

  const defaultSeriesType = useMemo(() => {
    if (chartType === CostReportChartType.AREA) {
      return 'area';
    }
    if (chartType === CostReportChartType.BAR) {
      return 'bar';
    }
    return 'line';
  }, [chartType]);

  const renderConfig = (chartConfig: ChartConfig, index: number) => {
    const { config } = chartConfig;
    const { dataKey } = config;

    if (dataKey === undefined) {
      return null;
    }

    const isForecastPoint = (
      entry: TickChartDataPoint<T>,
      entryIndex: number
    ) => {
      const isForecast =
        entry.forecast ||
        entry.isForecast ||
        (forecastStartIndex !== undefined &&
          forecastStartIndex >= 0 &&
          entryIndex >= forecastStartIndex);

      if (!isForecast) {
        return false;
      }

      // For bar charts (daily granularity), today's projected bar should remain fully opaque
      if (
        chartType === CostReportChartType.BAR &&
        timezone &&
        !isPastDataPoint(entry.timestamp, timezone)
      ) {
        return false;
      }

      return true;
    };

    const key = `${chartConfig.type}-${index}`;
    const commonProps = {
      dataKey,
      stackId: chartConfig.stackId,
      xAxisId: chartConfig.xAxisId,
      yAxisId: chartConfig.yAxisId,
      hide: config.hide,
      tooltipConfig: {
        displayName: config.name,
        unit: config.unit as string | undefined,
        hidden: hideSeriesInTooltip,
      },
      isAnimationActive,
    };

    if (chartConfig.type === CostReportChartType.AREA) {
      return (
        <Area<TickChartDataPoint<T>>
          key={key}
          {...commonProps}
          fill={config.fill || 'blue.500'}
          fillOpacity={config.fillOpacity}
          getFillByPoint={(entry, entryIndex) => {
            if (!isForecastPoint(entry, entryIndex)) {
              return undefined;
            }
            return {
              fillOpacity: 0.3,
            };
          }}
          stroke={config.stroke}
          strokeWidth={config.strokeWidth}
          type={config.type}
          dot={config.dot ?? (isE2E() ? HiddenDot : false)}
          activeDot={config.activeDot}
        />
      );
    }

    if (chartConfig.type === CostReportChartType.BAR) {
      return (
        <Bar<TickChartDataPoint<T>>
          key={key}
          {...commonProps}
          fill={config.fill || 'blue.500'}
          stroke={config.stroke}
          barSize={config.barSize}
          maxBarSize={config.maxBarSize}
          getBarPropsByPoint={(entry, entryIndex) => {
            if (!isForecastPoint(entry, entryIndex)) {
              return {};
            }
            return {
              fillOpacity: 0.3,
              strokeOpacity: 0.3,
            };
          }}
          getActiveBarPropsByPoint={(entry, entryIndex) => {
            if (!isForecastPoint(entry, entryIndex)) {
              return {};
            }
            return {
              fillOpacity: 0.3,
              strokeOpacity: 0.3,
            };
          }}
        />
      );
    }

    return (
      <Line<TickChartDataPoint<T>>
        key={key}
        {...commonProps}
        stroke={config.stroke || config.fill || 'blue.500'}
        strokeWidth={config.strokeWidth}
        strokeDasharray={config.strokeDasharray}
        getStrokeByPoint={(entry, entryIndex) => {
          if (!isForecastPoint(entry, entryIndex)) {
            return undefined;
          }
          return {
            strokeOpacity: 0.3,
          };
        }}
        type={config.type}
        connectNulls={config.connectNulls}
        dot={config.dot ?? (isE2E() ? HiddenDot : false)}
        activeDot={config.activeDot}
      />
    );
  };

  return (
    <Box display="flex" height={height} sx={sx} data-testid={testId}>
      <ChartContainer<TickChartDataPoint<T>>
        data={chartData}
        isLoading={isLoading}
        fallbackChartType={defaultSeriesType}
        margin={{ top: containerMarginTop }}
        sx={{
          width: '100%',
          ...chartSx,
        }}
        syncId={syncId || 'cost-over-time'}
        zoomProps={{
          xAxisDataKey: 'chartTimestampUnix',
          useZoomState: useCostReportZoomState,
        }}
        barConfig={barConfig}
        analyzeWithOpsPilot={analyzeWithOpsPilot}
      >
        {areaConfigs.map(renderConfig)}
        <ChartGrid hideVertical />
        {nonAreaConfigs.map(renderConfig)}

        <YAxis<TickChartDataPoint<T>>
          tickCount={5}
          type="number"
          allowDecimals
          padding={{ top: 30 }}
          {...yAxisProps}
        />
        <TimeXAxis<TickChartDataPoint<T>>
          unixDataKey="chartTimestampUnix"
          timezone={timezone}
          {...xAxisProps}
        />

        {children}
        <ChartTooltip<TickChartDataPoint<T>>
          testId="data-popper"
          header={tooltipHeader}
          footer={tooltipFooter}
          alwaysActive={tooltipAlwaysActive}
          TooltipComponent={TooltipComponent}
          formatValue={(value) => {
            return tooltipFormatValue ? tooltipFormatValue(value) : value;
          }}
        />
      </ChartContainer>
    </Box>
  );
};
